/**
 * Centralized Google Analytics tracking utility
 * 
 * Features:
 * - Automatically appends environment suffix to button IDs (e.g., _dev, _uat, _prod)
 * - Uses transport_type: 'beacon' for reliability during navigation/form submits
 * - Logs events to console in development for debugging
 * - Gracefully handles missing gtag (doesn't break the app)
 */

/**
 * Get the current environment name
 * Returns: 'development', 'staging', 'uat', or 'production'
 */
function getEnvironment(): string {
  return import.meta.env.VITE_HIGHLIGHT_ENV || 'development';
}

/**
 * Track a button click event in Google Analytics
 * 
 * @param buttonId - Base button identifier (e.g., 'conclusion_submit', 'edit_conclusion')
 * @param extraParams - Additional event parameters to send (optional)
 * 
 * @example
 * trackButtonClick('conclusion_submit', { tab: 'Conclusion' });
 * // Sends in production: 
 * //   button_id: 'conclusion_submit'
 * //   environment: 'production'
 */
export function trackButtonClick(buttonId: string, extraParams: Record<string, any> = {}) {
  if (typeof window === 'undefined') return;

  const environment = getEnvironment();
  
  // Log in development for debugging
  if (import.meta.env.DEV) {
    console.log('[GA Track]', {
      button_id: buttonId,
      environment,
      ...extraParams,
    });
  }

  const gtag = (window as any).gtag;
  
  if (!gtag) {
    console.warn('[GA] gtag not available, event not sent:', {
      button_id: buttonId,
      environment,
      ...extraParams,
    });
    return;
  }

  const payload = {
    button_id: buttonId,
    environment, // Send environment as separate parameter for two-dimension tracking
    ...extraParams,
    transport_type: 'beacon', // Ensures hits aren't lost during navigation
  };

  gtag('event', 'custom_button_click', payload);
}
